import type { ColorScheme } from './types';

/**
 * The user's appearance PREFERENCE — what the Theme setting stores. Web and
 * mobile share this file so the two clients resolve it identically; each
 * only injects how it applies the result (web: the `.dark` class on
 * `<html>`; mobile: NativeWind's `colorScheme.set`) and where it keeps the
 * preference (web: localStorage; mobile: AsyncStorage) — both under
 * {@link APPEARANCE_STORAGE_KEY}.
 */
export type Appearance = 'light' | 'dark' | 'system';

/** Storage key on both clients. Web's `useLocalStorage('theme')` predates this file. */
export const APPEARANCE_STORAGE_KEY = 'theme';

/**
 * Whether `system` (follow the OS) is offered and honoured, on every client.
 * ON since 2026-10-19 (dark mode is ready on mobile): a first visit follows the
 * OS, and System is a Theme option beside Light and Dark. Off, the app is Light
 * until the user picks Dark and a stored `system` reads as Light — the value
 * stays in {@link Appearance} so preferences parse either way.
 */
export const FOLLOWS_SYSTEM = true;

/** A first visit: the OS while {@link FOLLOWS_SYSTEM} is on, else Light. */
export const DEFAULT_APPEARANCE: Appearance = FOLLOWS_SYSTEM ? 'system' : 'light';

/** The Theme setting's options, in display order — the same list on every client. */
export const APPEARANCE_OPTIONS: readonly Appearance[] = FOLLOWS_SYSTEM
  ? ['light', 'dark', 'system']
  : ['light', 'dark'];

/**
 * Analytics fired when the user picks a theme. `system` fires nothing, as on
 * web since the setting shipped — the event names are the existing ones.
 */
export const APPEARANCE_EVENTS: Partial<Record<Appearance, string>> = {
  dark: 'Dark-Mode-Enable',
  light: 'Dark-Mode-Disable',
};

export const isAppearance = (value: unknown): value is Appearance =>
  value === 'light' || value === 'dark' || value === 'system';

/**
 * A stored preference → an {@link Appearance}. Tolerates web's
 * `useLocalStorage` JSON encoding (`'"dark"'`), `null` (nothing stored) and
 * anything unrecognised, which falls back to {@link DEFAULT_APPEARANCE}.
 */
export function parseAppearance(raw: string | null | undefined): Appearance {
  if (raw == null) return DEFAULT_APPEARANCE;
  const value = raw.replace(/^"|"$/g, '');
  return isAppearance(value) ? effectiveAppearance(value) : DEFAULT_APPEARANCE;
}

/**
 * The preference as the app honours it: `system` becomes the default while
 * {@link FOLLOWS_SYSTEM} is off. Every read and write goes through this, so a
 * value stored by an older build can never make a client follow the OS.
 */
export function effectiveAppearance(preference: Appearance): Appearance {
  return preference === 'system' && !FOLLOWS_SYSTEM ? DEFAULT_APPEARANCE : preference;
}

/** The scheme to paint: the preference, or the OS when it is `system` (and that is on). */
export function resolveAppearance(
  preference: Appearance,
  systemPrefersDark: boolean,
): ColorScheme {
  const effective = effectiveAppearance(preference);
  if (effective === 'system') return systemPrefersDark ? 'dark' : 'light';
  return effective;
}
